import { useEffect, useMemo } from "react"

import { Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import EmptyComponent from "../../../../Components/Empty"
import Table from "../../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../../constants/user-routes"
import { ReceiptLookupList } from "../../../ReceiptPage/components/ReceiptLookupList"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../../redux/deflectAnalytics.slice"
import { activityColumns } from "../activityColumns"
import ResultTag from "../ResultTag"
import Section from "../Section"

const { Text } = Typography

// dashboard/lookups/ ignores page_size for now and always answers 20, so the list is also cut here.
const PAGE_SIZE = 10

// Below this the eight columns would wrap their values; narrower cards scroll the table instead.
const RECENT_TABLE_SCROLL = { x: 880 }

// A phone card carries the two columns the Receipt list has no use for: the descriptor, cut to one
// line with the full value on hover, and the result.
const renderCardDetails = (record) => (
  <Flex justify="space-between" align="center" gap={8}>
    <Text ellipsis={{ tooltip: record.descriptor }} style={{ minWidth: 0 }}>
      {record.descriptor || "—"}
    </Text>
    <ResultTag value={record.result} />
  </Flex>
)

// "Recent lookups": the newest lookups in this page's calendar and programs, with the Lookup
// activity columns and no sort (RCA-11-06, RCA-11-08). "View All" opens the Receipt page itself.
const RecentLookups = ({ getData }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const result = useSelector(selectDeflectSection(DEFLECT_SECTION.LOOKUPS))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.LOOKUPS))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.LOOKUPS))

  const rows = useMemo(() => result?.results?.slice(0, PAGE_SIZE) ?? [], [result])
  const columns = useMemo(() => activityColumns({ t, sortable: false }), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])
  const refetching = fetching && !firstLoad

  useEffect(() => {
    getData(DEFLECT_SECTION.LOOKUPS, { page_size: PAGE_SIZE })
  }, [getData])

  return (
    <Section
      title={t("Recent lookups")}
      tooltip={t("The most recent bank inquiries, newest first.")}
      onViewAll={result?.count ? () => navigate(RoutesPaths.receipts.all) : undefined}
    >
      {isMobile ? (
        <Spin spinning={refetching}>
          <ReceiptLookupList
            loading={firstLoad}
            data={rows}
            withOutPagination
            skeletonCount={PAGE_SIZE}
            renderDetails={renderCardDetails}
          />
        </Spin>
      ) : rows.length || firstLoad ? (
        // The flat card-table header, as on Repeat cardholders.
        <Table
          className="chart-table"
          dataSource={firstLoad ? skeletonRows(PAGE_SIZE) : rows}
          columns={firstLoad ? loadingColumns : columns}
          rowKey={firstLoad ? "key" : "id"}
          loading={refetching}
          withOutPagination
          scroll={RECENT_TABLE_SCROLL}
        />
      ) : (
        <Spin spinning={refetching}>
          <EmptyComponent type="no_data_available" />
        </Spin>
      )}
    </Section>
  )
}

export default RecentLookups
